import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ObjectConfigMappingService } from 'src/app/Common/ObjectConfigMapping/ObjectConfigMapping.service';
import { ObjectConfigMappingByMenuCodeDTO } from 'src/app/Common/ObjectConfigMapping/ObjectConfigMapping.interface';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { ValueListItemDTO } from 'src/app/Common/ValueListService/ValueListItem.interface';
import { GenericFormUICService } from 'src/app/Common/UICServices/FormUIC/form-uic.service';
import { GenericFormUICDTO } from 'src/app/Common/UICServices/FormUIC/form-uic.interface';
import { UicFormComponent } from 'src/app/Shared/uic-form/uic-form.component';
import { ensureNumber, ensureString } from 'src/app/Shared/customFunctions/CustomFunctions';

import { QuotationService } from 'src/app/CRM/quotation/quotation.service';
import { QuotationDetailService } from 'src/app/CRM/quotation/quotation-detail/quotation-detail.service';
import { PurchaseOrderService } from 'src/app/purchase/purchase-order/purchase-order.services';
import { PurchaseOrderDetailService } from 'src/app/purchase/purchase-order/purchase-order-detail/purchase-order-detail.services';
import { PurchaseReleaseService } from 'src/app/purchase/purchase-release/purchase-release.service';
import { PurchaseReleaseDetailService } from 'src/app/purchase/purchase-release/purchase-release-detail/purchase-release-detail.service';
import { IndentMasterService } from 'src/app/purchase/indent-master/indent-master.service';
import { IndentDetailService } from 'src/app/purchase/indent-master/indent-detail/indent-detail.service';
import { SalesOrderService } from 'src/app/sale/sales-order/sales-order.service';
import { SalesOrderDetailService } from 'src/app/sale/sales-order/sales-order-detail/sales-order-detail.service';
import { SalesReleaseService } from 'src/app/sale/sales-release/sales-release.service';
import { SalesReleaseDetailService } from 'src/app/sale/sales-release/sales-release-detail/sales-release-detail.service';
import { PurchaseService } from 'src/app/purchase/purchase/purchase.service';
import { PurchaseDetailService } from 'src/app/purchase/purchase/purchase-detail/purchase-detail.service';
import { SalesMasterService } from 'src/app/sale/sales/sales.service';
import { SalesDetailService } from 'src/app/sale/sales/sales-detail/sales-detail.service';
import { SalesIndentMasterService } from 'src/app/sale/indent-master/indent-master.service';
import { SalesIndentDetailService } from 'src/app/sale/indent-master/indent-detail/indent-detail.service';
import { DebitDetailService } from 'src/app/purchase/debit-note-master/debit-detail/debit.detail.service';
import { CreditDetailService } from 'src/app/sale/credit-note-master/credit-detail/credit detail.service';
import { DebitNoteService } from 'src/app/purchase/debit-note-master/debit-note-master.service';
import { CreditNoteService } from 'src/app/sale/credit-note-master/credit-note-master.service';


interface ImportDocumentOption {
  id: number;
  label: string;
}

interface SourceConfig {
  formConfigMasterType: string;
  search: (term: string) => Observable<ImportDocumentOption[]>;
  fetch: (documentId: number) => Observable<any[]>;
}

interface TargetFieldSpec {
  itemRef: 'itemId' | 'masterId';
  qtyField?: string;          // 'qty' | 'quantity'
  priceField?: string;        // 'price' | 'rate' | 'unitPrice'
  qtyTypeField?: string;      // 'qtyType' | 'unit'
  discountField?: string;     // always sent as a TOTAL (per-unit * qty)
  discountRateField?: string;
  taxField?: string;          // 'tax' | 'taxAmount'
  totalAmountField?: string;  // 'totalAmount'
  gstRateField?: string;
  hasGstSplit?: boolean;
  hasRefFields?: boolean;
  otherDetailCount?: number;
  // Some targets (e.g. erp.SalesIndentDetail) have a SECOND, separately
  // NOT-NULL qty/qtyType pair ("requested" qty, distinct from the ordered
  // qty) that no import source actually collects. When set, these mirror
  // qtyField/qtyTypeField's resolved values onto their own column names so
  // the insert never sends NULL for them.
  reqQtyField?: string;
  reqQtyTypeField?: string;
  // Fallback used for qtyTypeField/reqQtyTypeField when the source row has
  // no qtyType/unit value at all (e.g. Quotation's GenericFormUIC config
  // doesn't expose a qtyType field), so a NOT-NULL qtyType-style column
  // still gets a sensible value instead of an empty string.
  defaultQtyType?: string;
  extra?: Record<string, any>;
}

interface TargetConfig {
  idParamName: string;
  itemsFieldName: string;
  fieldSpec: TargetFieldSpec;
  upsert: (payload: any) => Observable<any>;
}

/**
 * Generic, tenant-aware "Import" button for detail screens.
 *
 * Unlike ImportCrossInvoicesButtonComponent (kept as-is for its original
 * client with a hardcoded source-document list), this component:
 *  - is gated end-to-end by ObjectConfigMapping (ObjectCode 'impbtn' by
 *    default, checked via `hasAccess`, not just presence in the list)
 *  - sources its dropdown of importable document types from a
 *    tenant-scoped Value List ('GenericImportSourceType', shared across every
 *    screen that hosts this button)
 *  - sources its editable grid's field/column config from a tenant-scoped
 *    GenericFormUIC config, one masterType per SOURCE TYPE — not per screen
 *  - resolves EVERY source→target combination through two small, declarative
 *    registries (`sourceConfigs`, `targetConfigs`) plus an `allowedCombos`
 *    allow-list, instead of one hand-written submit/map method pair per
 *    combination. A single generic `mapRowToTargetItem()` handles the
 *    field-name differences between tables (Qty/Quantity, Price/Rate/
 *    UnitPrice, Tax/TaxAmount, QtyType/Unit, TotalAmount/Amount, ItemId/
 *    MasterId) by reading a small per-target `TargetFieldSpec` instead of
 *    duplicating near-identical mapping code per combination.
 */
@Component({
  selector: 'app-generic-detail-import-button',
  templateUrl: './generic-detail-import-button.component.html',
  styleUrls: ['./generic-detail-import-button.component.css']
})
export class GenericDetailImportButtonComponent implements OnInit {

  @Input({ required: true }) MasterId: number = 0;
  @Input({ required: true }) MasterType: string = '';
  @Input() disabled: boolean = false;

  /** ObjectConfigMaster code that gates this button. Defaults to 'impbtn'. */
  @Input() objectCode: string = 'impbtn';

  @Input() sourceTypeVlCode: string = '';

  User = inject(UserLoginService).User();
  private OCMService = inject(ObjectConfigMappingService);
  private VLService = inject(ValueListService);
  private GFormUICService = inject(GenericFormUICService);
  private route = inject(ActivatedRoute);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);

  // Source-side services (document search + candidate-line fetch)
  private quotationService = inject(QuotationService);
  private quotationDetailService = inject(QuotationDetailService);
  private purchaseOrderService = inject(PurchaseOrderService);
  private purchaseOrderDetailService = inject(PurchaseOrderDetailService);
  private purchaseReleaseService = inject(PurchaseReleaseService);
  private purchaseReleaseDetailService = inject(PurchaseReleaseDetailService);
  private purIndentMasterService = inject(IndentMasterService);
  private purIndentDetailService = inject(IndentDetailService);
  private salesOrderService = inject(SalesOrderService);
  private salesOrderDetailService = inject(SalesOrderDetailService);
  private salesReleaseService = inject(SalesReleaseService);
  private salesReleaseDetailService = inject(SalesReleaseDetailService);
  private purchaseMasterService = inject(PurchaseService);
  private purchaseDetailService = inject(PurchaseDetailService);
  private salesMasterService = inject(SalesMasterService);
  private salesDetailService = inject(SalesDetailService);
  private salesIndentMasterService = inject(SalesIndentMasterService);
  private salesIndentDetailService = inject(SalesIndentDetailService);

  // Target-side services (bulk upsert) not already covered above
  private debitDetailService = inject(DebitDetailService);
  private creditDetailService = inject(CreditDetailService);

  // Source-only master search services (no dedicated search-by-number
  // endpoint yet — search() falls back to a client-side filter, see below).
  private debitNoteMasterService = inject(DebitNoteService);
  private creditNoteMasterService = inject(CreditNoteService);

  /**
   * One entry per importable SOURCE document type. Adding a new source type
   * here (plus a `GenericImportSourceType` Value List item + a matching
   * `GenericImport_<X>` GenericFormUIC config table) is all that's needed to
   * make it selectable and fetchable — whether it does anything on a given
   * screen is then governed by `allowedCombos`.
   */
  private readonly sourceConfigs: Record<string, SourceConfig> = {
    Quotation: {
      formConfigMasterType: '[CRM].[uic].[GenericImport_Quotation]',
      search: (term) => this.quotationService.QuotationMaster_SearchByQuotationNumber({
        quotationNumber: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(q => ({ id: q.quotationId, label: q.quotationNumber })))),
      fetch: (documentId) => this.quotationDetailService.QuotationDetail_ReadByQuotationIdForImport({
        quotationId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    PurchaseOrder: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_PurchaseOrder]',
      search: (term) => this.purchaseOrderService.PurchaseOrder_SearchByPurchaseOrderNo({
        purchaseOrderNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(po => ({ id: po.purchaseOrderId, label: po.purchaseOrderNo })))),
      // Reuses the SAME endpoint the legacy add-via-purchase-order-button
      // (inside ImportCrossInvoicesButtonComponent) already calls — its
      // dedup is quantity-based (Qty - already-imported Qty > 0), not a
      // boolean "already imported" flag like the Quotation proc.
      fetch: (documentId) => this.purchaseOrderDetailService.PurchaseOrderDetail_ReadByPurchaseOrderIdForImport({
        purchaseOrderId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    PurchaseRelease: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_PurchaseRelease]',
      search: (term) => this.purchaseReleaseService.PurchaseRelease_SearchByReleaseNo({
        releaseNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(pr => ({ id: pr.releaseId, label: pr.releaseNo })))),
      fetch: (documentId) => this.purchaseReleaseDetailService.PurchaseReleaseDetail_ReadByReleaseIdForImport({
        releaseId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    // Source key MUST match the tenant's Value List vliCode exactly:
    // "PurchaseIndent" (not the internal "PurIndent" abbreviation used
    // elsewhere in this codebase for the host Purchase Indent screen's own
    // MasterType — that's a separate, unrelated string).
    PurchaseIndent: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_PurchaseIndent]',
      search: (term) => this.purIndentMasterService.IndentMaster_SearchByPurIndentNo({
        searchText: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map((pi: any) => ({ id: pi.indentId, label: pi.indentNo })))),
      fetch: (documentId) => this.purIndentDetailService.IndentDetail_ReadByIndentIdForImport({
        indentId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    SalesOrder: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_SalesOrder]',
      search: (term) => this.salesOrderService.SalesOrder_SearchByOrderNo({
        salesOrderNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(so => ({ id: so.salesOrderId, label: so.salesOrderNo })))),
      fetch: (documentId) => this.salesOrderDetailService.SalesOrderDetail_ReadBySalesOrderIdForImport({
        salesOrderId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    SalesRelease: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_SalesRelease]',
      search: (term) => this.salesReleaseService.SalesRelease_SearchByReleaseNo({
        releaseNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(sr => ({ id: sr.releaseId, label: sr.releaseNo })))),
      fetch: (documentId) => this.salesReleaseDetailService.SalesReleaseDetail_ReadByReleaseIdForImport({
        releaseId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    // These two feed DebitNote/CreditNote from the original invoice — the
    // tenant needs to ALSO add "PurchaseInvoice"/"SalesInvoice" Value List
    // items under GenericImportSourceType plus matching
    // GenericImport_PurchaseInvoice / GenericImport_SalesInvoice
    // GenericFormUIC config tables before these will show up/work; the
    // wiring here is ready the moment that config exists.
    PurchaseInvoice: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_PurchaseInvoice]',
      search: (term) => this.purchaseMasterService.PurchaseMaster_SearchByInvoiceNo({
        invoiceNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(p => ({ id: p.invoiceId, label: p.invoiceNo })))),
      fetch: (documentId) => this.purchaseDetailService.PurchaseDetail_ReadByInvoiceIdForImport({
        invoiceId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    SalesInvoice: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_SalesInvoice]',
      search: (term) => this.salesMasterService.SalesMaster_SearchByInvoiceNo({
        invoiceNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(s => ({ id: s.invoiceId, label: s.invoiceNo })))),
      fetch: (documentId) => this.salesDetailService.SalesDetail_ReadByInvoiceIdForImport({
        invoiceId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    // Challans share the exact same erp.SalesMaster/erp.SalesDetail tables as
    // Invoice — reuse SalesInvoice's search/fetch verbatim, just under the
    // Challan masterType/config table.
    SalesChallan: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_SalesChallan]',
      search: (term) => this.salesMasterService.SalesMaster_SearchByInvoiceNo({
        invoiceNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(s => ({ id: s.invoiceId, label: s.invoiceNo })))),
      fetch: (documentId) => this.salesDetailService.SalesDetail_ReadByInvoiceIdForImport({
        invoiceId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    PurchaseChallan: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_PurchaseChallan]',
      search: (term) => this.purchaseMasterService.PurchaseMaster_SearchByInvoiceNo({
        invoiceNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map(p => ({ id: p.invoiceId, label: p.invoiceNo })))),
      fetch: (documentId) => this.purchaseDetailService.PurchaseDetail_ReadByInvoiceIdForImport({
        invoiceId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    // SalesIndent/CreditNote/DebitNote now have real search-by-number backend
    // endpoints (SalesIndentMaster_SearchByIndentNo, CreditNoteMaster_SearchByCreditNoteNo,
    // DebitNoteMaster_SearchByDebitNoteNo), so search() hits those directly
    // instead of the old client-side-filtered ReadByTenantId fallback.
    SalesIndent: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_SalesIndent]',
      search: (term) => this.salesIndentMasterService.SalesIndentMaster_SearchByIndentNo({
        indentNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map((i: any) => ({ id: i.indentId, label: i.indentNo })))),
      fetch: (documentId) => this.salesIndentDetailService.SalesIndentDetail_ReadByIndentIdForImport({
        indentId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    CreditNote: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_CreditNote]',
      // NOTE: the backing SP's search parameter is currently typed INT even
      // though CreditNoteNo is varchar(50) — searching a non-numeric credit
      // note number will fail with a SQL conversion error until that SP
      // parameter is changed to VARCHAR(50)/NVARCHAR(50) on the DB side.
      search: (term) => this.creditNoteMasterService.CreditNoteMaster_SearchByCreditNoteNo({
        creditNoteNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map((i: any) => ({ id: i.creditNoteId, label: i.creditNoteNo })))),
      fetch: (documentId) => this.creditDetailService.CreditDetail_ReadByCreditNoteIdForImport({
        creditNoteId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
    DebitNote: {
      formConfigMasterType: '[ERP].[uic].[GenericImport_DebitNote]',
      // Same caveat as CreditNote above — the SP's @DebitNoteNo parameter is
      // typed INT even though DebitNoteNo is varchar(50).
      search: (term) => this.debitNoteMasterService.DebitNoteMaster_SearchByDebitNoteNo({
        debitNoteNo: term || '', tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => (res.items || []).map((i: any) => ({ id: i.debitNoteId, label: i.debitNoteNo })))),
      fetch: (documentId) => this.debitDetailService.DebitDetail_ReadByDebitNoteIdForImport({
        debitNoteId: documentId, masterId: this.MasterId, masterType: this.MasterType,
        tenantId: this.User.companyId, tenantBranchId: this.User.loggedInBranchId
      }).pipe(map(res => res.items || []))
    },
  };

  /**
   * One entry per importable TARGET detail table, keyed by an internal
   * "target key" (NOT directly the screen's MasterType, since several
   * MasterTypes share one target table — e.g. PurchaseInvoice and
   * PurchaseChallan both write to erp.PurchaseDetail). See
   * `targetKeyByMasterType` for the MasterType → target-key mapping.
   */
  private readonly targetConfigs: Record<string, TargetConfig> = {
    PurchaseDetail: {
      idParamName: 'invoiceId',
      itemsFieldName: 'purchaseDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'price', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'tax',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15, extra: { masterId: 0, masterType: '', isDefault: 0 }
      },
      upsert: (payload) => this.purchaseDetailService.PurchaseDetail_UpsertBulk(payload)
    },
    SalesDetail: {
      idParamName: 'invoiceId',
      itemsFieldName: 'salesDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'price', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'tax',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15, extra: { itemType: '' }
      },
      upsert: (payload) => this.salesDetailService.SalesDetail_UpsertBulk(payload)
    },
    PurchaseOrderDetail: {
      idParamName: 'purchaseOrderId',
      itemsFieldName: 'purchaseOrderDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'price', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15, extra: { isDefault: 0 }
        // erp.PurchaseOrderDetail.ReqQty/ReqQtyType are nullable, plain storage columns with no
        // derivation from qty/qtyType — intentionally NOT wired into reqQtyField/reqQtyTypeField
        // here (unlike SalesIndentDetail below, whose ReqQty/ReqQtyType are NOT NULL and require
        // a mirrored value on every insert).
      },
      upsert: (payload) => this.purchaseOrderDetailService.PurchaseOrderDetail_UpsertBulk(payload)
    },
    SalesOrderDetail: {
      idParamName: 'salesOrderId',
      itemsFieldName: 'salesOrderDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'rate', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'taxAmount',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15
      },
      upsert: (payload) => this.salesOrderDetailService.SalesOrderDetail_UpsertBulk(payload)
    },
    DebitNoteDetail: {
      idParamName: 'debitNoteId',
      itemsFieldName: 'debitNoteDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'price', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'tax',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: false,
        otherDetailCount: 15, extra: { roundOff: 0 }
      },
      upsert: (payload) => this.debitDetailService.DebitDetail_UpsertBulk(payload)
    },
    CreditNoteDetail: {
      idParamName: 'creditNoteId',
      itemsFieldName: 'creditNoteDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'rate', qtyTypeField: 'unit',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'tax',
        totalAmountField: 'amount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15, extra: { roundOff: 0 }
      },
      upsert: (payload) => this.creditDetailService.CreditDetail_UpsertBulk(payload)
    },
    QuotationDetail: {
      idParamName: 'quotationId',
      itemsFieldName: 'quotationDetails',
      fieldSpec: {
        itemRef: 'masterId', qtyField: 'quantity', priceField: 'unitPrice', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'tax',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 6, extra: { itemName: '', description: '' }
      },
      upsert: (payload) => this.quotationDetailService.QuotationDetail_UpsertBulk(payload)
    },
    SalesIndentDetail: {
      idParamName: 'indentId',
      itemsFieldName: 'salesIndentDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', priceField: 'price', qtyTypeField: 'qtyType',
        discountField: 'discount', discountRateField: 'discountRate', taxField: 'taxAmount',
        totalAmountField: 'totalAmount', gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15,
        // erp.SalesIndentDetail.ReqQty/ReqQtyType are NOT NULL, but no import
        // source (Quotation included) collects a separate "requested" qty —
        // mirror the resolved qty/qtyType onto them so the insert never sends
        // NULL. 'Unit' matches the tenant's own default for this field on the
        // Sales Indent's regular add/edit form.
        reqQtyField: 'reqQty', reqQtyTypeField: 'reqQtyType', defaultQtyType: 'Unit'
      },
      upsert: (payload) => this.salesIndentDetailService.SalesIndentDetail_UpsertBulk(payload)
    },
    // erp.PurchaseReleaseDetail/erp.SalesReleaseDetail have NO price/discount/
    // tax/totalAmount columns at all (release documents only track item + qty
    // + GST split + ref-back fields) — priceField/discountField/taxField/
    // totalAmountField are left undefined in both specs below on purpose;
    // mapRowToTargetItem() simply skips any field whose spec key is unset.
    PurchaseReleaseDetail: {
      idParamName: 'releaseId',
      itemsFieldName: 'purchaseReleaseDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', qtyTypeField: 'qtyType',
        gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15
      },
      upsert: (payload) => this.purchaseReleaseDetailService.PurchaseReleaseDetail_UpsertBulk(payload)
    },
    SalesReleaseDetail: {
      idParamName: 'releaseId',
      itemsFieldName: 'salesReleaseDetails',
      fieldSpec: {
        itemRef: 'itemId', qtyField: 'qty', qtyTypeField: 'qtyType',
        gstRateField: 'gstRate', hasGstSplit: true, hasRefFields: true,
        otherDetailCount: 15
      },
      upsert: (payload) => this.salesReleaseDetailService.SalesReleaseDetail_UpsertBulk(payload)
    },
  };

  /** Which target-config key handles a given screen's MasterType. Screens
   *  whose MasterType has no entry here simply can't be a target yet
   *  (e.g. PurIndent — nothing precedes it in the flow, by design). */
  private readonly targetKeyByMasterType: Record<string, string> = {
    PurchaseInvoice: 'PurchaseDetail',
    PurchaseChallan: 'PurchaseDetail',
    SalesInvoice: 'SalesDetail',
    SalesChallan: 'SalesDetail',
    PurchaseOrder: 'PurchaseOrderDetail',
    SalesOrder: 'SalesOrderDetail',
    DebitNote: 'DebitNoteDetail',
    CreditNote: 'CreditNoteDetail',
    Quotation: 'QuotationDetail',
    // Added: these 3 screens host the button but had no target-key entry at
    // all, so ANY source on them hit "isn't wired up yet" regardless of
    // allowedCombos (SalesIndent is the combo the user hit explicitly).
    SalesIndent: 'SalesIndentDetail',
    PurchaseRelease: 'PurchaseReleaseDetail',
    SalesRelease: 'SalesReleaseDetail',
  };

  /**
   * The actual "which source can import into which target" allow-list —
   * mirrors the natural procurement/sales document flow:
   *   PurchaseIndent → PurchaseOrder → PurchaseInvoice/PurchaseChallan
   *   Quotation → SalesOrder, Quotation/SalesOrder/SalesRelease → SalesInvoice/SalesChallan
   *   PurchaseInvoice → DebitNote, SalesInvoice → CreditNote
   * A source not listed for a given target-key just falls through to the
   * "isn't wired up yet" message — same behavior as before, just data-driven
   * instead of a hardcoded if/else chain.
   */
  private readonly allowedCombos: Record<string, string[]> = {
    PurchaseIndent: ['PurchaseOrderDetail'],
    // PurchaseOrder can also feed a PurchaseRelease (goods released against
    // an order), mirroring SalesOrder -> SalesReleaseDetail below.
    PurchaseOrder: ['PurchaseDetail', 'PurchaseReleaseDetail'],
    // PurchaseRelease and PurchaseInvoice can both also feed back INTO a
    // PurchaseOrder (e.g. "re-order" from a prior release/invoice), mirroring
    // the Sales-side SalesRelease/SalesInvoice -> SalesOrderDetail combos below.
    PurchaseRelease: ['PurchaseDetail', 'PurchaseOrderDetail'],
    // Quotation can also be converted straight into a Sales Indent (the
    // combo the user hit explicitly), alongside its existing targets.
    Quotation: ['SalesDetail', 'SalesOrderDetail', 'SalesIndentDetail'],
    // SalesOrder can also feed a SalesRelease (goods released against an
    // order), mirroring PurchaseOrder -> PurchaseReleaseDetail above.
    SalesOrder: ['SalesDetail', 'SalesReleaseDetail'],
    // SalesRelease and SalesInvoice can both also feed back INTO a
    // SalesOrder (e.g. "re-order" from a prior release/invoice).
    SalesRelease: ['SalesDetail', 'SalesOrderDetail'],
    PurchaseInvoice: ['DebitNoteDetail', 'PurchaseOrderDetail'],
    SalesInvoice: ['CreditNoteDetail', 'SalesOrderDetail'],
    // Challans mirror their Invoice counterparts exactly (same source tables).
    PurchaseChallan: ['DebitNoteDetail', 'PurchaseOrderDetail'],
    SalesChallan: ['CreditNoteDetail', 'SalesOrderDetail'],
    // SalesIndent is the sales-side mirror of PurchaseIndent -> PurchaseOrderDetail:
    // an indent can be converted into a formal SalesOrder, or quoted first.
    SalesIndent: ['SalesOrderDetail', 'QuotationDetail'],
    // CreditNote/DebitNote are left with no outgoing target here — they're
    // terminal documents in this flow, not something you import FROM into
    // another document. They're still registered above in `sourceConfigs`
    // so their field config / document picker / grid preview all work; only
    // "Import Selected" would show the "isn't wired up yet" message unless a
    // real forward target is identified.
  };

  /** Every selectable source type is just whatever's configured for this tenant AND has a search/fetch registered above. */
  get documentPickerSupportedVliCodes(): string[] {
    return Object.keys(this.sourceConfigs);
  }

  /** Emitted after a successful bulk import so the host detail screen can refresh its own grid. */
  @Output() onImportCompleted = new EventEmitter<void>();

  objectList: ObjectConfigMappingByMenuCodeDTO[] = [];

  /** True once the ObjectConfigMapping lookup has returned (success or fail). */
  isAccessChecked: boolean = false;

  /** Resolved visibility for `objectCode`, based on `hasAccess`, not mere presence. */
  hasAccess: boolean = false;

  /** Tenant-specific list of importable source-document types for this screen's MasterType. */
  sourceTypeOptions: ValueListItemDTO[] = [];
  isLoadingSourceTypes: boolean = false;

  /** The source type currently selected from the dropdown (drives GenericFormUIC lookup). */
  selectedSourceType: ValueListItemDTO | null = null;

  /** Tenant-configured, visible, ordered field config for the selected source type. */
  selectedFormUIC: GenericFormUICDTO[] = [];
  isLoadingFormConfig: boolean = false;
  formConfigError: string = '';
  /** Resolved GenericFormUIC masterType (fully qualified table name) for the selected source type, for display. */
  resolvedMasterType: string = '';

  @ViewChild('sourceTypeConfigModal', { static: true }) sourceTypeConfigModalTemplate!: ElementRef;
  private configModalRef!: NgbModalRef;

  /** Normalized {id, label} regardless of source type — see ImportDocumentOption. */
  documentSearchResults: ImportDocumentOption[] = [];
  isSearchingDocuments: boolean = false;
  selectedDocumentId: number | null = null;

  /** Raw candidate-line DTOs from whichever source type's fetch endpoint ran —
   *  shape varies by source type (QuotationDetailDTO, PurchaseOrderDetailDTO, ...). */
  candidateLines: any[] = [];
  isLoadingCandidateLines: boolean = false;
  candidateLinesError: string = '';

  /**
   * Editable, row-selectable import grid. The uic-form instance is
   * (re)created each time `candidateLines` is repopulated (its host
   * *ngIf resets between document selections), so `onImportFormReady` seeds
   * it fresh via `patchWithDependencies(rows, { forceMultiple: true })` every
   * time.
   *
   * Captured directly from the `(formReady)` event's template-reference-
   * variable argument rather than an `@ViewChild('importUicFormRef')` query,
   * since that query wasn't reliably resolving with the grid nested two
   * `*ngIf` levels deep inside an ng-bootstrap modal template.
   */
  private activeImportFormRef?: UicFormComponent;
  isImportGridReady: boolean = false;

  /** Rows the user ticked in the grid, captured on "Import Selected" and submitted to the target's bulk-upsert endpoint. */
  selectedImportRows: any[] = [];
  importSelectionMessage: string = '';
  isSubmittingImport: boolean = false;

  /**
   * True as soon as at least one row's `isSelected` checkbox is ticked.
   * "Import Selected" stays disabled until both the grid has finished
   * seeding (`isImportGridReady`) AND this is true.
   */
  hasAnySelectedRow: boolean = false;
  private importGridSubscriptions: Subscription[] = [];

  ngOnInit(): void {
    if (!this.sourceTypeVlCode?.trim()) {
      this.sourceTypeVlCode = 'GenericImportSourceType';
    }

    let r = this.route;
    while (r.firstChild) r = r.firstChild;

    const currentPath = r.routeConfig?.path ?? '';
    const parentPath = r.parent?.routeConfig?.path ?? '';

    const normalize = (path: string) =>
      path.split('/').find(p => !p.startsWith(':')) ?? '';

    const menuCode = normalize(currentPath) || normalize(parentPath);

    this.checkObjectAccess(menuCode);
  }

  private checkObjectAccess(menuCode: string): void {
    const data = {
      menuCode: menuCode,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.OCMService.ObjectConfigMapping_readByMenuCode(data).subscribe({
      next: (res) => {
        this.objectList = res.list || [];
        this.hasAccess = this.isObjectCodeVisible(this.objectCode);
        this.isAccessChecked = true;

        if (this.hasAccess) {
          this.loadSourceTypeOptions();
        }
      },
      error: (err) => {
        console.error(`[GenericDetailImportButton] Failed to load ObjectConfigMapping for menuCode "${menuCode}":`, err);
        this.objectList = [];
        this.hasAccess = false;
        this.isAccessChecked = true;
      }
    });
  }

  /**
   * Loads this tenant's configured import source types — the ValueListItem
   * rows under the single shared 'GenericImportSourceType' VlCode — via the
   * existing "Add ValueList" / "Add ValueList Item" admin screens. Same list
   * on every screen that hosts this button; no per-screen setup needed.
   */
  private loadSourceTypeOptions(): void {
    this.isLoadingSourceTypes = true;

    const data = {
      vlCode: this.sourceTypeVlCode,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId
    };

    this.VLService.ValueListItem_readByVlCode(data).subscribe({
      next: (res) => {
        const items = res.items || [];

        this.sourceTypeOptions = items
          .filter(item => Number(item.isActive) === 1)
          .sort((a, b) => (a.displaySeq ?? 0) - (b.displaySeq ?? 0));

        this.isLoadingSourceTypes = false;
      },
      error: (err) => {
        console.error(`[GenericDetailImportButton] Failed to load Value List "${this.sourceTypeVlCode}":`, err);
        this.sourceTypeOptions = [];
        this.isLoadingSourceTypes = false;
      }
    });
  }

  /**
   * Resolves the GenericFormUIC masterType (fully qualified table name) for
   * a given source type. Scoped by source type only (not by screen), so the
   * same field/column config for "importing a Purchase Order line" is
   * reused everywhere a tenant allows importing from Purchase Orders.
   * Returns '' if no source config is registered yet for this vliCode.
   */
  private resolveFormConfigMasterType(item: ValueListItemDTO): string {
    return this.sourceConfigs[item.vliCode]?.formConfigMasterType || '';
  }

  /**
   * Loads the tenant's GenericFormUIC field/column config for the selected
   * source type, then opens a checkpoint modal listing the resolved fields
   * (label, input type, editable state), and kicks off the document search
   * if this source type has one registered.
   */
  onSourceTypeSelected(item: ValueListItemDTO): void {
    this.selectedSourceType = item;
    this.selectedFormUIC = [];
    this.formConfigError = '';
    this.isLoadingFormConfig = true;

    this.documentSearchResults = [];
    this.selectedDocumentId = null;
    this.candidateLines = [];
    this.candidateLinesError = '';
    this.resetImportGridState();

    this.configModalRef = this.modalService.open(this.sourceTypeConfigModalTemplate, {
      size: 'xl',
      scrollable: true,
      windowClass: 'generic-import-modal-wide'
    });

    const masterType = this.resolveFormConfigMasterType(item);
    this.resolvedMasterType = masterType;

    if (!masterType) {
      this.isLoadingFormConfig = false;
      this.formConfigError = `No GenericFormUIC table wired up yet for source type "${item.vliCode}".`;

      if (this.isDocumentPickerSupportedFor(item.vliCode)) {
        this.searchDocuments('');
      }
      return;
    }

    this.GFormUICService.GenericFormUIC_ReadByTenantId({
      masterType,
      tenantId: this.User.companyId
    }).subscribe({
      next: (res) => {
        this.selectedFormUIC = (res.items || [])
          .filter(f => Number(f.isUIVisible) === 1)
          .sort((a, b) => (a.displaySeq ?? 0) - (b.displaySeq ?? 0));

        if (this.selectedFormUIC.length === 0) {
          this.formConfigError = `No GenericFormUIC fields configured for masterType "${masterType}" for this tenant.`;
        }

        this.isLoadingFormConfig = false;
      },
      error: (err) => {
        console.error(`[GenericDetailImportButton] Failed to load GenericFormUIC for masterType "${masterType}":`, err);
        this.selectedFormUIC = [];
        this.formConfigError = `Failed to load field config for masterType "${masterType}".`;
        this.isLoadingFormConfig = false;
      }
    });

    if (this.isDocumentPickerSupportedFor(item.vliCode)) {
      this.searchDocuments('');
    }
  }

  private isDocumentPickerSupportedFor(vliCode: string): boolean {
    return !!this.sourceConfigs[vliCode];
  }

  /** Whether the selected source type has a document picker + fetch wired up yet. */
  get isDocumentPickerSupported(): boolean {
    return !!this.selectedSourceType && this.isDocumentPickerSupportedFor(this.selectedSourceType.vliCode);
  }

  /**
   * Searches for a specific source document by number, same UX as the
   * legacy add-via-purchase-order-button: an ng-select typeahead at the top
   * of the modal, loaded once with an empty term and re-queried as the user
   * types. Fully data-driven off `sourceConfigs` — no per-source-type
   * branching here anymore.
   */
  searchDocuments(term: string): void {
    const config = this.selectedSourceType ? this.sourceConfigs[this.selectedSourceType.vliCode] : undefined;
    if (!config) {
      this.isSearchingDocuments = false;
      return;
    }

    this.isSearchingDocuments = true;

    config.search(term).subscribe({
      next: (results) => {
        this.documentSearchResults = results;
        this.isSearchingDocuments = false;
      },
      error: (err) => {
        console.error(`[GenericDetailImportButton] Failed to search "${this.selectedSourceType?.vliCode}" documents:`, err);
        this.documentSearchResults = [];
        this.isSearchingDocuments = false;
      }
    });
  }

  /**
   * Fetches candidate lines for the chosen source document, excluding lines
   * already imported into a target of this screen's MasterType (dedup
   * handled server-side by each source's own *_ReadByXIdForImport proc).
   * Fully data-driven off `sourceConfigs`.
   */
  onDocumentSelected(documentId: number | null): void {
    this.selectedDocumentId = documentId;
    this.candidateLines = [];
    this.candidateLinesError = '';
    this.resetImportGridState();

    const config = this.selectedSourceType ? this.sourceConfigs[this.selectedSourceType.vliCode] : undefined;

    if (!documentId || !config) {
      return;
    }

    this.isLoadingCandidateLines = true;

    config.fetch(documentId).subscribe({
      next: (items) => {
        this.candidateLines = items || [];

        if (this.candidateLines.length === 0) {
          this.candidateLinesError = 'No importable lines found for this document (all lines may already be imported).';
        }

        this.isLoadingCandidateLines = false;
      },
      error: (err) => {
        console.error('[GenericDetailImportButton] Failed to fetch candidate lines:', err);
        this.candidateLines = [];
        this.candidateLinesError = 'Failed to load lines for the selected document.';
        this.isLoadingCandidateLines = false;
      }
    });
  }

  /**
   * Fired once the newly (re)created uic-form's initial single-row FormGroup
   * has been built from `selectedFormUIC`. At that point we seed it with the
   * real candidate lines via `patchWithDependencies(rows, { forceMultiple: true })`,
   * which expands it into one form instance per row. Each row is
   * pre-selected (`isSelected: 1`) since every candidate line has already
   * passed the server-side dedup filter — the user can uncheck any they
   * don't want.
   */
  onImportFormReady(ready: boolean, formRef: UicFormComponent): void {
    this.isImportGridReady = false;
    this.activeImportFormRef = ready ? formRef : undefined;

    if (!ready || !formRef || this.candidateLines.length === 0) {
      return;
    }

    const rows = this.candidateLines.map(line => ({ ...line, isSelected: 1 }));

    formRef.patchWithDependencies(rows, { forceMultiple: true }).then(() => {
      this.isImportGridReady = true;
      this.watchRowSelectionChanges(formRef);
    });
  }

  /** Every row instance in the grid, uic-form's `enableMultipleForms` or not. */
  private getImportFormInstances(formRef: UicFormComponent): { formGroup: FormGroup }[] {
    return formRef.enableMultipleForms && formRef.formInstances?.length > 0
      ? formRef.formInstances
      : [{ formGroup: formRef.gFormGroup }];
  }

  /**
   * Subscribes to every row's `isSelected` checkbox so `hasAnySelectedRow`
   * (and therefore the "Import Selected" button's disabled state) updates
   * live as the user ticks/unticks rows, instead of only being computed once
   * when the grid finishes seeding.
   */
  private watchRowSelectionChanges(formRef: UicFormComponent): void {
    this.clearImportGridSubscriptions();

    const instances = this.getImportFormInstances(formRef);
    const recompute = () => this.recomputeHasAnySelectedRow(formRef);

    instances.forEach(instance => {
      const control = instance.formGroup.get('isSelected');
      if (!control) {
        return;
      }
      this.importGridSubscriptions.push(control.valueChanges.subscribe(recompute));
    });

    recompute();
  }

  private recomputeHasAnySelectedRow(formRef: UicFormComponent): void {
    this.hasAnySelectedRow = this.getImportFormInstances(formRef)
      .some(instance => Number(instance.formGroup.get('isSelected')?.value) === 1);
  }

  private clearImportGridSubscriptions(): void {
    this.importGridSubscriptions.forEach(sub => sub.unsubscribe());
    this.importGridSubscriptions = [];
  }

  /** Resets all grid state — called whenever the source type or document changes. */
  private resetImportGridState(): void {
    this.clearImportGridSubscriptions();
    this.isImportGridReady = false;
    this.hasAnySelectedRow = false;
    this.selectedImportRows = [];
    this.importSelectionMessage = '';
    this.isSubmittingImport = false;
    this.activeImportFormRef = undefined;
  }

  /**
   * Reads back every row's current (possibly edited) values from the
   * uic-form's per-row FormGroups, keeping only the ones the user left
   * ticked, then submits them to the target screen's bulk-upsert endpoint.
   *
   * The FormGroup only contains the fields configured as visible in
   * GenericFormUIC — it does NOT carry `detailId`/`masterId`/the source
   * document's own id, which the target's bulk endpoint needs for the item
   * reference and dedup tracking (refMasterId/refDetailId). Since rows can't
   * be added/removed in this grid, `formInstances[i]` always lines up with
   * `candidateLines[i]`, so we merge each row's edited values back onto its
   * original full source-row DTO before submitting.
   */
  onImportSelectedClick(): void {
    const formRef = this.activeImportFormRef;
    if (!formRef) {
      console.error('[GenericDetailImportButton] "Import Selected" clicked but no active uic-form reference is set — the grid may not have finished seeding yet.');
      this.importSelectionMessage = 'The grid isn\'t ready yet. Please wait a moment and try again.';
      return;
    }

    try {
      const instances = this.getImportFormInstances(formRef);
      const rows = instances.map((instance, i) => ({
        ...(this.candidateLines[i] as any),
        ...instance.formGroup.getRawValue()
      }));

      this.selectedImportRows = rows.filter(row => Number(row.isSelected) === 1);

      if (this.selectedImportRows.length === 0) {
        this.importSelectionMessage = 'No rows selected.';
        return;
      }

      this.submitSelectedRows();
    } catch (err) {
      console.error('[GenericDetailImportButton] Failed to read selected rows from the grid:', err);
      this.importSelectionMessage = 'Something went wrong reading the selected rows. Check the console for details.';
    }
  }

  /**
   * Dispatches the currently-selected rows to the correct bulk-upsert
   * endpoint for the current (sourceType → MasterType) combination, fully
   * data-driven off `targetKeyByMasterType` + `allowedCombos` +
   * `targetConfigs` — no per-combination submit method anymore. Add a new
   * source→target combination by adding one line to `allowedCombos`
   * (assuming both the source and target are already registered above).
   */
  private submitSelectedRows(): void {
    const sourceVliCode = this.selectedSourceType?.vliCode;
    const targetKey = this.targetKeyByMasterType[this.MasterType];
    const isComboAllowed = !!sourceVliCode && !!targetKey && (this.allowedCombos[sourceVliCode] || []).includes(targetKey);

    if (!isComboAllowed) {
      this.importSelectionMessage = `Importing "${this.selectedSourceType?.vliName}" into "${this.MasterType}" isn't wired up yet.`;
      return;
    }

    const target = this.targetConfigs[targetKey];
    if (!target) {
      this.importSelectionMessage = `No target handler registered for "${this.MasterType}".`;
      return;
    }

    this.isSubmittingImport = true;

    // `app-uic-discount`/`app-uic-tax` only ever run — and only ever turn a
    // stored per-line TOTAL into a genuinely per-unit amount — when their
    // GenericFormUIC field is actually rendered (isUIVisible=1; see
    // uic-form.component.html's `*ngIf="inputs.isUIVisible === 1"` wrapper).
    // Every GenericImport_<X> config seen so far hides "discount"/"tax"
    // (they're not meaningful raw toggle inputs inside a read-only import
    // preview grid), so the widgets never mount and `row.discount`/`row.tax`
    // simply pass through whatever the SOURCE table's own Discount/Tax
    // columns already hold — which is itself a per-line TOTAL, same
    // convention as `totalAmount`. Multiplying that by qty again double-
    // counts quantity (e.g. qty=3 inflates it 3x). Only multiply by qty when
    // the field is genuinely visible for this source type, since only then
    // has the widget actually recomputed it down to a true per-unit amount.
    const isDiscountFieldVisible = this.selectedFormUIC.some(f => f.dbFeildName === 'discount');
    const isTaxFieldVisible = this.selectedFormUIC.some(f => f.dbFeildName === 'tax');

    const refMasterId = ensureNumber(this.selectedDocumentId);
    const items = this.selectedImportRows.map(row => this.mapRowToTargetItem(row, target.fieldSpec, {
      refMasterType: sourceVliCode as string,
      refMasterId,
      refDetailId: ensureNumber(this.pick(row, [
        'detailId', 'purchaseOrderDetailId', 'salesOrderDetailId',
        'purchaseReleaseDetailId', 'salesReleaseDetailId', 'indentDetailId'
      ])),
      discountIsPerUnit: isDiscountFieldVisible,
      taxIsPerUnit: isTaxFieldVisible
    }));

    const payload: any = {
      [target.idParamName]: this.MasterId,
      tenantId: this.User.companyId,
      tenantBranchId: this.User.loggedInBranchId,
      actionUser: this.User.userId.toString(),
      [target.itemsFieldName]: items
    };

    target.upsert(payload).subscribe({
      next: () => {
        this.isSubmittingImport = false;
        this.toastr.success(`${this.selectedImportRows.length} line(s) imported from ${this.selectedSourceType?.vliName}.`);
        this.onImportCompleted.emit();
        this.closeConfigModal();
      },
      error: (err) => {
        this.isSubmittingImport = false;
        console.error(`[GenericDetailImportButton] Bulk import into "${this.MasterType}" failed:`, err);
        this.toastr.error('Failed to import the selected lines. Please try again.');
        this.importSelectionMessage = 'Import failed. Please try again.';
      }
    });
  }

  /** Returns the first defined/non-null value found on `row` among `keys`, in order. */
  private pick(row: any, keys: string[]): any {
    for (const key of keys) {
      if (row?.[key] !== undefined && row?.[key] !== null) {
        return row[key];
      }
    }
    return undefined;
  }

  /**
   * THE generic row → target-item mapper. Replaces what would otherwise be
   * one hand-written mapXRowToYBulkItem() method per (source, target) pair.
   * Most columns are identical in spirit across every detail table (an item
   * reference, a quantity, a price, a discount, a tax, a total, a GST rate,
   * up to 15 "other detail" free-text columns) but differ in exact property
   * name per table — resolved here via alias lists (qty/quantity,
   * price/rate/unitPrice, tax/taxAmount, qtyType/unit, totalAmount/amount,
   * itemId/masterId) plus the target's own `TargetFieldSpec` describing
   * which of ITS OWN property names each canonical concept should land on
   * (or `undefined` if that target has no such column at all, e.g.
   * PurchaseReleaseDetail has no price/discount/tax whatsoever).
   *
   * `discount`/`tax` are always sent as a TOTAL — every target's own
   * UpsertBulk stored procedure expects that. Preferred path: recompute both
   * fresh from `discountRate`/`gstRate` against the row's CURRENT price/qty
   * (every GenericImport_<X> config seen so far keeps those two rate fields
   * VISIBLE even though the raw discount/tax AMOUNT fields are hidden) —
   * this stays correct no matter what the user edits in the import grid
   * afterwards, since a rate is qty-independent. Fallback path (only when no
   * rate is present at all): whether `row.discount`/`row.tax` need to be
   * MULTIPLIED by qty depends on whether `app-uic-discount`/`app-uic-tax`
   * actually ran: those widgets only mount when their field is visible
   * (isUIVisible=1) and, when they do, they patch a genuine PER-UNIT amount
   * (see uic-discount.component.ts / uic-tax.component.ts). Every
   * GenericImport_<X> config seen so far hides both amount fields, so the
   * widgets never run and the row's values are already whatever per-line
   * TOTAL the source table itself stored at fetch time — note that this
   * fallback total goes stale if the user changes Qty/Price afterwards,
   * which is exactly why the rate-based path above is preferred whenever
   * possible. `ref.discountIsPerUnit`/`ref.taxIsPerUnit` (derived from the
   * tenant's actual visible-field config, not a hardcoded assumption) decide
   * which fallback case applies per source type.
   *
   * Tax breakdown (CGST/SGST/IGST/UTGST/Cess) is intentionally left at 0 for
   * every target — this grid has no intra-/inter-state notion, so only the
   * flat `tax`/`gstRate` travel; targets that compute GST server-side
   * (PurchaseOrderDetail, SalesOrderDetail, DebitNoteDetail,
   * crm.QuotationDetail) recompute the real split from `gstRate` anyway.
   */
  private mapRowToTargetItem(
    row: any,
    spec: TargetFieldSpec,
    ref: {
      refMasterType: string; refMasterId: number; refDetailId: number;
      discountIsPerUnit: boolean; taxIsPerUnit: boolean;
    }
  ): any {
    const qty = ensureNumber(this.pick(row, ['qty', 'quantity']));
    const price = ensureNumber(this.pick(row, ['price', 'rate', 'unitPrice']));
    const rawDiscount = ensureNumber(row.discount);
    const rawTax = ensureNumber(this.pick(row, ['tax', 'taxAmount']));
    const discountRate = ensureNumber(row.discountRate);
    const gstRate = ensureNumber(row.gstRate);

    // Every GenericImport_<X> config seen so far keeps discountRate/gstRate
    // VISIBLE (isUIVisible=1) even though the raw discount/tax AMOUNT fields
    // are hidden. Rates are qty-independent, but the raw discount/tax amount
    // is whatever was computed ONCE at fetch time against the source line's
    // ORIGINAL qty/price — since app-uic-discount/app-uic-tax never mount
    // for a hidden field, that amount does NOT recompute when the user edits
    // Qty/Price afterwards in the import grid, so passing it through (even
    // scaled by the discountIsPerUnit/taxIsPerUnit heuristic below) goes
    // stale the moment qty changes. Recomputing straight from the rate
    // against the row's CURRENT qty/price is correct either way, so prefer
    // it whenever a real rate is present; only fall back to the raw
    // amount-based heuristic when no rate is available at all (e.g. a flat
    // "Net" discount with no % equivalent supplied by the source).
    let discountTotal: number;
    if (discountRate > 0) {
      discountTotal = Number((price * qty * discountRate / 100).toFixed(2));
    } else {
      discountTotal = ref.discountIsPerUnit ? Number((rawDiscount * qty).toFixed(2)) : rawDiscount;
    }

    let taxTotal: number;
    if (gstRate > 0) {
      const taxableAmount = Number((price * qty - discountTotal).toFixed(2));
      taxTotal = Number((taxableAmount * gstRate / 100).toFixed(2));
    } else {
      taxTotal = ref.taxIsPerUnit ? Number((rawTax * qty).toFixed(2)) : rawTax;
    }

    const item: any = {
      // Every target's UpsertBulk item DTO uses "remarks" — but the SOURCE
      // row's own property name varies: crm.QuotationDetail (uniquely) calls
      // this column "Description" (row.description), every other source
      // table calls it "Remarks" (row.remarks). Picking both covers every
      // combo with one line instead of a per-source override.
      remarks: ensureString(this.pick(row, ['remarks', 'description'])) || '',
      displaySeq: 0,
    };

    if (spec.itemRef === 'masterId') {
      item.masterId = ensureNumber(this.pick(row, ['masterId', 'itemId']));
      item.masterType = '';
    } else {
      item.itemId = ensureNumber(this.pick(row, ['itemId', 'masterId']));
    }

    const resolvedQtyType = ensureString(this.pick(row, ['qtyType', 'unit'])) || spec.defaultQtyType || '';

    if (spec.qtyField) item[spec.qtyField] = qty;
    if (spec.priceField) item[spec.priceField] = price;
    if (spec.qtyTypeField) item[spec.qtyTypeField] = resolvedQtyType;
    // "Requested" qty/qtyType mirror the resolved qty/qtyType above — no
    // import source distinguishes a separate requested quantity, so the
    // ordered qty is the only sensible value to carry over.
    if (spec.reqQtyField) item[spec.reqQtyField] = qty;
    if (spec.reqQtyTypeField) item[spec.reqQtyTypeField] = resolvedQtyType;
    if (spec.discountField) item[spec.discountField] = discountTotal;
    if (spec.discountRateField) item[spec.discountRateField] = ensureNumber(row.discountRate);
    if (spec.taxField) item[spec.taxField] = taxTotal;
    if (spec.totalAmountField) item[spec.totalAmountField] = ensureNumber(this.pick(row, ['totalAmount', 'amount']));
    if (spec.gstRateField) item[spec.gstRateField] = ensureNumber(row.gstRate);

    if (spec.hasGstSplit) {
      item.cgstAmt = 0;
      item.sgstAmt = 0;
      item.igstAmt = 0;
      item.utgstAmt = 0;
      item.cessRate = 0;
      item.cessAmt = 0;
    }

    if (spec.hasRefFields) {
      item.refMasterType = ref.refMasterType;
      item.refMasterId = ref.refMasterId;
      item.refDetailId = ref.refDetailId;
    }

    const otherCount = spec.otherDetailCount ?? 15;
    for (let i = 1; i <= otherCount; i++) {
      item['otherDetail' + i] = ensureString(row['otherDetail' + i]) || '';
    }

    if (spec.extra) {
      Object.assign(item, spec.extra);
    }

    return item;
  }

  closeConfigModal(): void {
    this.clearImportGridSubscriptions();
    this.configModalRef?.close();
  }

  /**
   * Checks whether `code` is both configured for this menu AND has access
   * granted (hasAccess === 1) for the current tenant/branch — a stricter
   * check than the legacy component's "code exists in list" check.
   */
  isObjectCodeVisible(code: string): boolean {
    if (!code || !this.objectList?.length) {
      return false;
    }
    return this.objectList.some(ol =>
      ol.objectCode?.toLowerCase() === code.toLowerCase() && Number(ol.hasAccess) === 1
    );
  }
}
